import { ReactiveController } from 'lit';
import { FiltersHelper } from '../helpers/filters.helper';
import { StringConditions } from '@nirvana/shared/dtos/types/shared/filters';
import { getFilterConfigsByKey } from '../../../utils/filters.utils';
import { Filter, FilterKeys, FilterType, ViewType } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { FILTER_LIMIT_FOR_ADD_MORE, FilterConfigFrontend } from '../types';
import { CompanyFilterDashboardDataProvider } from '../../../ui/gzSingleSelectComplex/dataProvider/company/CompanyFilterDashboardDataProvider';
import { GetItemsResponse } from '../../../ui/gzSingleSelectComplex/types';
import { store } from '../../../redux/store';
import { disableLiveView, toggleLiveViewActiveState } from '../../redux/dashboards.ui.slice';
import { partition } from 'lodash';
import { getColumnKeyForFilter, getFilterWithDefaultValues } from '../../../grid/utils/filters';
import { setRefreshWidgets } from '../../redux/dashboards.widgets.slice';
import { GzDashboardFiltersBarWebComponent } from '../component/GzDashboardFiltersBarWebComponent';
import { resetFilters, setFilters } from '../../redux/dashboard.filters.slice';
import { API_TYPE_GZ } from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import { DisplayMode } from '../../../types/common/types';

export class GzDashboardFiltersController implements ReactiveController {
  host: GzDashboardFiltersBarWebComponent;

  constructor(host: GzDashboardFiltersBarWebComponent) {
    this.host = host;
    this.host.addController(this);
  }

  hostUpdate() {
    this.validate();
    this.useActiveCompany();
  }

  hostUpdated() {
    switch (this.host.displayMode) {
      case DisplayMode.DASHBOARD:
        this.updateCompanyFilter();
        this.updateTagsFilter();
        break;
      case DisplayMode.PDF:
      case DisplayMode.REPORT:
        break;
      default:
        break;
    }
  }

  handleFilterValueChange(key: FilterKeys, event: CustomEvent) {
    const filterDetails = getFilterConfigsByKey(this.host.filterConfigs, key) as FilterConfigFrontend;
    const newValue = this.getFilterValueFromEvent(filterDetails, event);
    this.setFilterByKey(key, newValue);

    //sticky company/active company
    if (key === FilterKeys.COMPANY && filterDetails.useActiveCompany && this.host.activeViewConfig.type === ViewType.DEFAULT && typeof this.host.dependencies.setActiveCompany === 'function') {
      this.host.dependencies.setActiveCompany(event.detail.selectedValue, event.detail.selectedLabel);
    }
  }

  onMoreFiltersUpdated(event: CustomEvent) {
    // Should add filters for all predefined columns & for the filters selected from "More".
    // We split the filters on whether they are predefined/mandatory or not
    const [predefinedFilters, existingOptionalFilters] = partition(this.host.filters, filter => {
      const filterKey = getColumnKeyForFilter(filter);
      const filterDetails = getFilterConfigsByKey(this.host.filterConfigs, filterKey) as FilterConfigFrontend;
      return this.isFilterPredefined(filterDetails);
    });

    const newFilterKeysFromSelector = event.detail.selectedValues || [];

    const newOptionalFilters = newFilterKeysFromSelector.map(filterKey => {
      const existingFilter = existingOptionalFilters.find(filterToKeep =>
        Object.keys(filterToKeep).includes(filterKey)
      );

      if (existingFilter) {
        return existingFilter;
      }

      const filterDetails = getFilterConfigsByKey(this.host.filterConfigs, filterKey) as FilterConfigFrontend;
      return getFilterWithDefaultValues(filterDetails);
    });

    const newFilters = predefinedFilters.concat(newOptionalFilters);
    this.setFilters(newFilters);
  }

  removeFilter(removedFilterKey: FilterKeys) {
    const newFilters = this.host.filters.filter(filter => getColumnKeyForFilter(filter) !== removedFilterKey);
    this.setFilters(newFilters);
  }

  public async resetFilters(): Promise<void> {
    await this.setActiveCompanyFromActiveViewConfig();
    store.dispatch(resetFilters());
    store.dispatch(toggleLiveViewActiveState(false));
    store.dispatch(setRefreshWidgets(true));
  }

  public async setActiveCompanyFromActiveViewConfig(): Promise<void> {
    const filterDetails = getFilterConfigsByKey(this.host.filterConfigs, FilterKeys.COMPANY) as FilterConfigFrontend;

    // If the company filter is set to use the active company
    if (filterDetails.useActiveCompany && this.host.activeViewConfig.type === ViewType.DEFAULT && typeof this.host.dependencies.setActiveCompany === 'function') {
      try {
        const filterValue = this.host.activeViewConfig.filters.find(filter => filter.company)?.company as StringConditions;
        const companyName = await CompanyFilterDashboardDataProvider.getCompanyNameById(filterValue?.equals || '');
        this.host.dependencies.setActiveCompany(filterValue?.equals, companyName);
      } catch (error: any) {
        if (error?.exceptionType === 'Exceptions\\BDConsoleException') {
          console.error(error);
          return;
        }
      }
    }
  }

  isFilterPredefined(filterDetails: FilterConfigFrontend) {
    // All filters are 'predefined' if we don't render the "More" selector
    if (!this.hasAddMoreSelector()) {
      return true;
    }

    return FiltersHelper.isFilterPredefinedByConfig(filterDetails);
  }

  getCompanyIdFromFilters(): string {
    const filterValue = this.host.filters.find(filter => filter.company)?.company as StringConditions;

    // The filters for forms should only allow a single company.
    return filterValue?.equals || '';
  }

  private async updateCompanyFilter() {
    if (!FiltersHelper.shouldDisplayCompanyFilter()) {
      /*
       This means we want the filter to be applied, but users can't select anything else. Used for customer-type users.
       Keep in mind that the filter isn't rendered in the DOM on this branch.
       */
      return;
    }
    const selectedValues: GetItemsResponse = {
      items: [],
      totalItemsCount: 0,
    };

    const company = this.getCompanyIdFromFilters();
    if (company) {
      try {
        const companyName = await CompanyFilterDashboardDataProvider.getCompanyNameById(company);
        selectedValues.items.push({
          label: companyName,
          value: company,
        });
        selectedValues.totalItemsCount = 1;
      } catch (error: any) {
        if (error?.exceptionType === 'Exceptions\\BDConsoleException') {
          console.error(error);
          return;
        }
      }
    }

    await this.host.$companyFilter.setFilterOptions(selectedValues);
    this.host.$companyFilter.value = company;
  }

  private async updateTagsFilter() {
    // This needs to be checked every rendering because the filter might be optional on other dashboards
    if (!this.host.tagsFilter) {
      return;
    }

    if (!this.host.tagsFilter.started) {
      this.host.tagsFilter.injectDependencies({
        api: { type: API_TYPE_GZ },
        translateFn: translateFn,
        isCloud: Application.Main.product.isCloud,
        permissions: {
          canHaveChildCompanies: Application.Main.canHaveChildCompanies(),
        },
      });

      await this.host.tagsFilter.start();
    }

    const tagsFilter = this.host.filters.find(filter => filter.tags);
    if (!tagsFilter) {
      return;
    }
    await this.host.tagsFilter.setSelectedItems((tagsFilter.tags as StringConditions).in || []);
  }

  private validate() {
    const dateFilter = this.host.filters.find(filter => filter.hasOwnProperty(FilterKeys.DATE_TIME));
    if (dateFilter && this.host.hasLiveView) {
      // This is intentional, we want this spec
      store.dispatch(disableLiveView());
      console.error('Date filter cannot work with live view');
    }
  }

  private getFilterValueFromEvent(filterDetails: FilterConfigFrontend, event: CustomEvent) {
    switch (filterDetails.type) {
      case FilterType.COMPANY:
        return this.getFilterValueForCompany(filterDetails, event);
      case FilterType.ENUM:
        return this.getFilterValueForEnumFilter(filterDetails, event);
      case FilterType.TAGS:
        return this.getFilterValueForTagsFilter(filterDetails, event);
      case FilterType.STRING:
      case FilterType.NUMBER:
        return this.getFilterValueForInputFilter(filterDetails, event);
      case FilterType.RANGE:
        return this.getFilterValueForRangeFilter(filterDetails, event);
      case FilterType.SELECT:
        return this.getFilterValueForSelectFilter(filterDetails, event);
      case FilterType.DATE:
      default:
        return event.detail || this.getFilterWithDefaultValues(filterDetails);
    }
  }

  private getFilterValueForCompany(filterDetails: FilterConfigFrontend, event: CustomEvent) {
    const companyId = event.detail.selectedValue;

    if (!companyId) {
      // this returns { in: { equals: '#companyId' } }
      const defaultValue = this.getFilterWithDefaultValues(filterDetails);
      return Object.values(defaultValue)[0];
    }

    // We enforce this because the Company filter for Dashboard filters can only allow one company selected
    return { equals: companyId };
  }

  private getFilterValueForEnumFilter(filterDetails: FilterConfigFrontend, event: CustomEvent) {
    const selectedValues = event.detail.selectedValues;
    return selectedValues.length ? { in: selectedValues } : this.getFilterWithDefaultValues(filterDetails);
  }

  private getFilterValueForTagsFilter(filterDetails: FilterConfigFrontend, event: CustomEvent) {
    const selectedValues = event.detail.selectedValues;
    return selectedValues ? { in: selectedValues } : this.getFilterWithDefaultValues(filterDetails);
  }

  private getFilterValueForInputFilter(filterDetails: FilterConfigFrontend, event: CustomEvent) {
    const value = event.detail.sourceEvent;

    if (value) {
      if (typeof value === 'number') {
        return { eq: value };
      } else {
        if (value.includes('*')) {
          return { regex: { pattern: value } };
        } else {
          return { startsWith: value };
        }
      }
    } else {
      return this.getFilterWithDefaultValues(filterDetails);
    }
  }

  private useActiveCompany(): void {
    const filterConfigs = getFilterConfigsByKey(this.host.filterConfigs, FilterKeys.COMPANY) as FilterConfigFrontend;
    if (filterConfigs.useActiveCompany && this.host.activeViewConfig.type === ViewType.DEFAULT && typeof this.host.dependencies.getActiveCompany === 'function') {
      this.setFilterByKey(FilterKeys.COMPANY, {equals: this.host.dependencies.getActiveCompany().companyId});
    }
  }

  private getFilterValueForRangeFilter(filterDetails: FilterConfigFrontend, event: CustomEvent) {
    const value = event.detail.sourceEvent;
    return value ? { range: value } : this.getFilterWithDefaultValues(filterDetails);
  }

  private getFilterValueForSelectFilter(filterDetails: FilterConfigFrontend, event: CustomEvent) {
    const selectedValue = event.detail.newValue?.value;
    return selectedValue ? { equals: selectedValue } : this.getFilterWithDefaultValues(filterDetails);
  }

  private setFilterByKey(key: FilterKeys, value: any): void {
    const newFilters = this.host.filters.map(filter => ({ ...filter }));
    this.changeFilterByKey(key, value, newFilters);
    this.setAdditionalFilters(key, newFilters);
    this.setFilters(newFilters);
  }

  private setAdditionalFilters(changedFilterKey: FilterKeys, newFilters: Array<Filter>) {
    switch (changedFilterKey) {
      case FilterKeys.COMPANY:
        this.changeFilterByKey(FilterKeys.TAGS, { in: [] }, newFilters);
        break;
      default:
        break;
    }
  }

  // This function changes the filter value by key in-place in the newFilters array
  private changeFilterByKey(key: FilterKeys, value: any, newFilters: Array<Filter>) {
    const filterObj = newFilters.find(filter => Object.keys(filter).includes(key));

    if (filterObj) {
      filterObj[key] = value;
    }
  }

  private setFilters(newFilters: Array<Filter>) {
    const unifiedFilterConfig = FiltersHelper.getUnifiedFilterConfigs(newFilters, this.host.filterConfigs);
    store.dispatch(setFilters(unifiedFilterConfig));
    store.dispatch(toggleLiveViewActiveState(false));
    store.dispatch(setRefreshWidgets(true));
  }

  private hasAddMoreSelector() {
    return this.host.filterConfigs.length > FILTER_LIMIT_FOR_ADD_MORE;
  }

  private getFilterWithDefaultValues(filterDetails: FilterConfigFrontend) {
    // We need this in order to not duplicate the filter key in the filter object on clear
    return getFilterWithDefaultValues(filterDetails)[filterDetails.key];
  }
}
